import client from './client';
import { blobErrorMessage, getDownloadFilename } from './modifiedDuty';

/**
 * Sick Hours (WCDP payroll) — read-only list of everyone with an APPROVED or PENDING
 * sick time-off request with hours in the pay period, plus a server-side Excel export.
 * Mirrors the API's GET /admin/timeOff/sickHours and POST /admin/timeOff/sickHours/export.
 */

export type SickTorStatus = 'APPROVED' | 'PENDING';
export type SickTimesheetStage = 'PENDING_APPROVAL' | 'PAYROLL_PENDING' | 'PAYROLL_APPROVED' | 'EXPORTED';
export type SickSuperSource = 'timesheet' | 'employee';
export type SickHoursExportSuffix = 'NeedsRate' | 'HasPending';

export interface SickHoursDay {
    /** YYYY-MM-DD */
    date: string;
    hours: number;
    torStatus: SickTorStatus;
    torID: number;
}

export interface SickHoursRow {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    /** The timesheet's supervisor, else the employee's own (source says which); null if neither. */
    superintendent: { id: number; name: string; source: SickSuperSource } | null;
    days: SickHoursDay[];
    approvedHours: number;
    pendingHours: number;
    totalHours: number;
    sickRate: number | null;
    /** null = Unsubmitted (no timesheet yet for the week). */
    timesheet: { id: number; stage: SickTimesheetStage } | null;
}

export interface SickHoursPayload {
    payPeriodID: number;
    weekNumber: number;
    accountingYear: number;
    /** YYYY-MM-DD */
    startDate: string;
    /** YYYY-MM-DD */
    endDate: string;
    rows: SickHoursRow[];
}

export function sickHoursErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { error?: string; message?: string } } }).response;
        if (response?.data?.error) return response.data.error;
        if (response?.data?.message) return response.data.message;
    }
    if (err instanceof Error) return err.message;
    return "Couldn't load sick hours for this week.";
}

export async function getSickHours(payPeriodID: number): Promise<SickHoursPayload> {
    const response = await client.get<SickHoursPayload>('/admin/timeOff/sickHours', {
        params: { payPeriodID },
    });
    return response.data;
}

/**
 * Downloads the Sick Hours .xlsx for exactly the rows on screen. `employeeIDs` must be
 * in display (filtered + sorted) order; `suffix` names the quick filter in the file name.
 * `fallbackName` is used when the response has no Content-Disposition.
 */
export async function exportSickHours(
    body: { payPeriodID: number; employeeIDs: number[]; suffix?: SickHoursExportSuffix },
    fallbackName: string,
): Promise<{ blob: Blob; fileName: string }> {
    try {
        const response = await client.post('/admin/timeOff/sickHours/export', body, {
            responseType: 'blob',
        });
        const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
        const fileName = getDownloadFilename(contentDisposition, fallbackName);
        return { blob: response.data as Blob, fileName };
    } catch (error: any) {
        console.error('Error exporting sick hours:', error);
        throw new Error(await blobErrorMessage(error, 'Failed to export sick hours'));
    }
}
